import * as utils from "./utils";
import { PLAN_DETAILS } from "./constants";

/**
 * What each plan allows, fetched once and held for the life of the tab.
 *
 * These used to live only in `PLAN_DETAILS`, which meant a limit could only be
 * changed by deploying this app — and that the dashboard and the marketing site
 * each kept their own copy, free to disagree. The API owns them now, and both
 * front ends read the same answer.
 *
 * The read is synchronous because every caller is: `hasReachedPropertiesLimit`
 * runs inside a render and cannot await. So the matrix is loaded once at
 * startup into a module-level cache, and `PLAN_DETAILS` is what is served until
 * it lands. That fallback is the shipped default rather than a guess, so the
 * first paint is correct and the fetch only makes it editable.
 */
export type PlanMatrix = Record<string, Record<string, number | boolean>>;

let matrix: PlanMatrix | null = null;
let inFlight: Promise<void> | null = null;

/** The shipped values, used until the API answers and if it never does. */
function fallbackMatrix(): PlanMatrix {
  const plans: PlanMatrix = {};
  PLAN_DETAILS.forEach(plan => {
    if (!plan?.plan) return;
    const features: Record<string, number | boolean> = {};
    plan.value.forEach((feature: any) => {
      features[feature.feature] =
        feature.limit !== undefined ? feature.limit : !!feature.isAllowedFeature;
    });
    plans[plan.plan] = features;
  });
  return plans;
}

/**
 * Fetch the matrix once. Safe to call from several places — later callers wait
 * on the first request rather than starting their own.
 */
export function loadPlanLimits(): Promise<void> {
  if (matrix) return Promise.resolve();
  if (inFlight) return inFlight;

  inFlight = (async () => {
    const result: any = await utils.request(null, "settings/plans/", "GET");
    if (result?.ok && result.data?.plans) {
      matrix = result.data.plans as PlanMatrix;
    }
    inFlight = null;
  })();

  return inFlight;
}

function currentMatrix(): PlanMatrix {
  return matrix ?? fallbackMatrix();
}

/**
 * The numeric cap for one plan and feature. -1 means unlimited.
 *
 * Returned as stored, including the -1. Callers must not compare it directly:
 * use `isWithinPlanLimit`, because 0 and -1 look alike to a careless `>=` and
 * mean the opposite.
 */
export function planFeatureLimit(planName: string, featureName: string): number {
  const value = currentMatrix()[planName]?.[featureName];
  return typeof value === "number" ? value : 0;
}

/** Whether a plan may use a feature that is simply on or off. */
export function planAllowsFeature(planName: string, featureName: string): boolean {
  const value = currentMatrix()[planName]?.[featureName];
  return typeof value === "boolean" ? value : false;
}

/**
 * Whether one more of something is allowed.
 *
 * The single place that knows -1 means unlimited, so no caller has to remember
 * it. Mirrors `is_within_limit` in the API, which is what actually enforces it.
 */
export function isWithinPlanLimit(limit: number, currentCount: number): boolean {
  if (limit === -1) return true;
  return currentCount < limit;
}
